if (!customElements.get('td-certification-grid')) {
  class TDCertificationGrid extends HTMLElement {
    static SELECTORS = {
      rail: '[data-td-certification-grid-rail]',
      viewport: '[data-td-certification-grid-viewport]',
      wrapper: '[data-td-certification-grid-wrapper]',
      content: '[data-td-certification-grid-content]',
      track: '[data-td-certification-grid-track]',
      image: '[data-td-certification-grid-image] img',
    };

    static MIN_DURATION = 14;
    static PIXELS_PER_SECOND = 60;

    constructor() {
      super();
      this.animationFrame = null;
      this.handleResize = this.handleResize.bind(this);
      this.handleModeChange = this.handleModeChange.bind(this);
      this.handleImageLoad = this.handleImageLoad.bind(this);
    }

    connectedCallback() {
      this.rail = this.querySelector(TDCertificationGrid.SELECTORS.rail);
      this.viewport = this.querySelector(TDCertificationGrid.SELECTORS.viewport);
      this.wrapper = this.querySelector(TDCertificationGrid.SELECTORS.wrapper);
      this.content = this.querySelector(TDCertificationGrid.SELECTORS.content);
      this.track = this.querySelector(TDCertificationGrid.SELECTORS.track);
      this.imageNodes = Array.from(this.querySelectorAll(TDCertificationGrid.SELECTORS.image));
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

      if (!this.rail || !this.viewport || !this.wrapper || !this.content || !this.track) {
        return;
      }

      window.addEventListener('resize', this.handleResize, { passive: true });
      this.bindMediaQueryListener(true);
      this.bindImageLoadListeners(true);

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleUpdate();
        });
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.bindMediaQueryListener(false);
      this.bindImageLoadListeners(false);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      this.resetLoop();
    }

    bindMediaQueryListener(shouldBind) {
      const method = shouldBind ? 'addEventListener' : 'removeEventListener';
      const legacyMethod = shouldBind ? 'addListener' : 'removeListener';

      if (typeof this.reduceMotionQuery?.[method] === 'function') {
        this.reduceMotionQuery[method]('change', this.handleModeChange);
      } else if (typeof this.reduceMotionQuery?.[legacyMethod] === 'function') {
        this.reduceMotionQuery[legacyMethod](this.handleModeChange);
      }
    }

    bindImageLoadListeners(shouldBind) {
      this.imageNodes.forEach((image) => {
        const method = shouldBind ? 'addEventListener' : 'removeEventListener';
        image[method]('load', this.handleImageLoad);
      });
    }

    handleResize() {
      this.scheduleUpdate();
    }

    handleModeChange() {
      this.scheduleUpdate();
    }

    handleImageLoad() {
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.animationFrame = requestAnimationFrame(() => {
        this.animationFrame = null;
        this.updateLoopState();
      });
    }

    // Measure rail/track (untransformed) so loop math stays stable while marquee runs.
    measure() {
      const styles = getComputedStyle(this);
      const readVar = (name) => parseFloat(styles.getPropertyValue(name)) || 0;

      return {
        gap: readVar('--td-certification-grid-gap-current'),
        trackWidth: this.track.getBoundingClientRect().width,
        available: Math.max(0, this.rail.clientWidth - readVar('--td-certification-grid-inline-offset-current') * 2),
      };
    }

    updateLoopState() {
      if (!this.rail || !this.viewport || !this.wrapper || !this.content || !this.track) {
        return;
      }

      const { gap, trackWidth, available } = this.measure();

      // Skip zero measurements from lazy layout (would restart a running marquee).
      if (trackWidth <= 0 || available <= 0) {
        return;
      }

      const loop = trackWidth - available > 1;

      // Ignore URL-bar / image-load resizes that don't change layout (avoids marquee jumps).
      const signature = [Math.round(available), Math.round(trackWidth), loop].join('|');

      if (signature === this.layoutSignature) {
        return;
      }

      this.layoutSignature = signature;
      this.resetLoop();

      if (!loop || this.isDesignMode || this.reduceMotionQuery.matches) {
        this.dataset.tdLoopActive = 'false';
        this.setScrollAffordance(loop);
        return;
      }

      const clone = this.content.cloneNode(true);
      clone.removeAttribute('data-td-certification-grid-content');
      clone.setAttribute('data-td-certification-grid-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      this.wrapper.appendChild(clone);

      const loopWidth = this.content.getBoundingClientRect().width + gap;
      const duration = Math.max(TDCertificationGrid.MIN_DURATION, loopWidth / TDCertificationGrid.PIXELS_PER_SECOND);

      this.style.setProperty('--td-certification-grid-loop-width', `${loopWidth}px`);
      this.style.setProperty('--td-certification-grid-duration', `${duration}s`);
      this.dataset.tdLoopActive = 'true';
      this.setScrollAffordance(false);
      this.restartAnimation();
    }

    setScrollAffordance(scrollable) {
      if (scrollable) {
        this.viewport.setAttribute('tabindex', '0');
      } else {
        this.viewport.removeAttribute('tabindex');
      }
    }

    resetLoop() {
      this.querySelectorAll('[data-td-certification-grid-clone]').forEach((clone) => clone.remove());
      this.style.removeProperty('--td-certification-grid-loop-width');
      this.style.removeProperty('--td-certification-grid-duration');
      this.wrapper.style.animation = 'none';
      this.wrapper.offsetHeight;
      this.wrapper.style.removeProperty('animation');
    }

    restartAnimation() {
      this.wrapper.style.animation = 'none';
      this.wrapper.offsetHeight;
      this.wrapper.style.removeProperty('animation');
    }
  }

  customElements.define('td-certification-grid', TDCertificationGrid);
}
